// Memory Manager — Namespaced localStorage for JARVIS V5
// Each domain (Trading, Teaching, Coding) reads ONLY its own namespace.
// Cross-domain contamination is impossible by design.

import { NS } from './AgentRegistry';

const PREFIX   = 'j5_';            // "j5" = JARVIS V5 namespace prefix
const MAX_KEYS = 500;              // Max keys per namespace before auto-cleanup

// Build the full localStorage key
const buildKey = (ns, name) => `${PREFIX}${ns}_${name}`;

// Get all keys belonging to a namespace
const nsKeys = (ns) => {
  const pfx = `${PREFIX}${ns}_`;
  const keys = [];
  for (let i = 0; i < localStorage.length; i++) {
    const k = localStorage.key(i);
    if (k && k.startsWith(pfx)) keys.push(k);
  }
  return keys;
};

export const MemoryManager = {

  // ── Write ────────────────────────────────────────────────────────────────
  write(ns, name, value) {
    try {
      // Auto-cleanup: if namespace getting too large, remove oldest 10%
      const existing = nsKeys(ns);
      if (existing.length >= MAX_KEYS) {
        existing
          .map(k => ({ k, ts: JSON.parse(localStorage.getItem(k) || '{}').ts || 0 }))
          .sort((a, b) => a.ts - b.ts)
          .slice(0, Math.floor(MAX_KEYS * 0.1))
          .forEach(({ k }) => localStorage.removeItem(k));
      }
      localStorage.setItem(buildKey(ns, name), JSON.stringify({ v: value, ts: Date.now() }));
      return true;
    } catch { return false; }
  },

  // ── Read ─────────────────────────────────────────────────────────────────
  read(ns, name, defaultValue = null) {
    try {
      const raw = localStorage.getItem(buildKey(ns, name));
      if (!raw) return defaultValue;
      return JSON.parse(raw).v;
    } catch { return defaultValue; }
  },

  // ── Read only if fresh (within maxAgeMs) ─────────────────────────────────
  readFresh(ns, name, maxAgeMs) {
    try {
      const raw = localStorage.getItem(buildKey(ns, name));
      if (!raw) return null;
      const parsed = JSON.parse(raw);
      return (Date.now() - parsed.ts) <= maxAgeMs ? parsed.v : null;
    } catch { return null; }
  },

  // ── Append item to an array (LIFO, max length enforced) ──────────────────
  append(ns, name, item, maxLen = 1000) {
    const arr = this.read(ns, name, []);
    this.write(ns, name, [item, ...arr].slice(0, maxLen));
  },

  // ── Remove a single key ───────────────────────────────────────────────────
  remove(ns, name) {
    localStorage.removeItem(buildKey(ns, name));
  },

  // ── Clear entire namespace ────────────────────────────────────────────────
  clearNS(ns) {
    nsKeys(ns).forEach(k => localStorage.removeItem(k));
  },

  // ── Clear temp memory (call on app start) ─────────────────────────────────
  clearTemp() {
    this.clearNS(NS.TEMP);
  },

  // ── Stats for one namespace ───────────────────────────────────────────────
  stats(ns) {
    const keys = nsKeys(ns);
    const sizeB = keys.reduce((s, k) => s + (localStorage.getItem(k) || '').length, 0);
    return { ns, keys: keys.length, sizeKB: +(sizeB / 1024).toFixed(1) };
  },

  // ── Stats for all namespaces ──────────────────────────────────────────────
  allStats() {
    return Object.values(NS).map(ns => this.stats(ns));
  },

  // ── Does key exist? ───────────────────────────────────────────────────────
  has(ns, name) {
    return localStorage.getItem(buildKey(ns, name)) !== null;
  },
};
